// ProgramWizard — Details → Sessions → Audience → Tracks & responsibilities →
// Review & publish. Nothing is written until Review: create program → PUT
// program audience rule → add program champions → create sessions → include
// explicit people → PUT per-session override rule → POST tracks → publish (or
// stop before publish for "Save as draft"). Audience/track rules are only
// DESCRIBED here (ConditionGroupBuilder emits the JSON tree); matching — and
// track membership — is the backend's (Preview calls /audience/preview with
// the tree). If a later step fails after the program exists, the wizard keeps
// the created ids so a retry never duplicates. Phase 4: the Tracks step also
// binds an optional completion policy / practical exercise per session (and
// per track, via TrackEditor) and lets the admin create a policy inline.
// Phase 5: a "Start" step offers a template gallery — instantiating posts
// /programs/from-template, closes the wizard and opens the new draft (the
// admin sets the audience there) — or a blank program (the flow below). The
// Details step carries an optional readiness policy (PUT after the program
// exists) and each session a "Critical for readiness" flag.
import { useT } from '../lib/i18n';
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, ChevronDown, ChevronUp, FilePlus2, LayoutTemplate, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/features/shared/components/ui/dialog';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { Stepper } from '@/features/shared/components/Stepper';
import { ConditionGroupBuilder, PersonPicker } from '@/features/shared/components/ConditionGroupBuilder';
import { validateRule, type RuleNode } from '@/features/shared/components/conditionGroup';
import { getApiErrorMessage } from '@/lib/apiError';
import type { AssignmentType, AudienceMode, AudiencePreview, DeliveryMode, Program, ProgramInput, ReadinessPolicy, SessionInput, TrackInput } from '@/services/learningService';
import {
  useAddChampion, useAudienceFacets, useAudiencePreview, useCompletionPolicies, useCreateCompletionPolicy, useCreateProgram, useCreateProgramType, useCreateSession, useCreateTrack,
  useIncludeParticipants, useProgramTypes, usePublishProgram, usePutProgramRule, usePutReadinessPolicy, usePutSessionRule,
} from '../api/hooks';
import { useRuleVocabulary } from '../lib/audienceRules';
import { browserTimezone, localInputToIso } from '../lib/format';
import { AssignmentTypeBadge } from './AssignmentTypeBadge';
import { AudiencePreviewPanel } from './AudiencePreviewPanel';
import { TrackEditor } from './TrackEditor';
import { PolicyEditor } from './PolicyEditor';
import { PolicyPracticeSelects } from './PolicyPracticeSelects';
import { modeLabel } from '../lib/completion';
import { TemplateGallery } from './TemplateGallery';
import { ReadinessPolicyEditor } from './ReadinessPolicyEditor';
import { useUnsavedGuard, usePendingGuard } from '@/features/shared/components/recovery/useFormGuards';

export interface SessionDraft {
  key: string;
  title: string;
  starts_local: string;
  ends_local: string;
  delivery_mode: DeliveryMode;
  location: string;
  meeting_url: string;
  trainer_label: string;
  objectives: string;
  assignment_type: AssignmentType | '';
  user_ids: string[];
  /** Optional per-session rule that overrides the program rule (collapsed by default). */
  override: boolean;
  override_rule: RuleNode | null;
  override_name: string;
  preview?: AudiencePreview | null;
  /** Phase 3: mint a Meet/Teams link on publish (virtual/hybrid, no pasted link). */
  generate_meeting_link: boolean;
  /** Phase 3: optional tracks, created via POST /sessions/{id}/tracks once the session exists. */
  tracks: TrackDraft[];
  /** Phase 4: optional session-level bindings ('' = none). Track bindings override them. */
  completion_policy_id?: string;
  practice_id?: string;
  /** Phase 5: counts toward readiness when the policy requires critical sessions. */
  critical?: boolean;
}
/** One track as the wizard holds it: the TrackInput the API will receive plus a local key. */
export interface TrackDraft extends TrackInput { key: string }
/** Program-scope champion, created via POST /champions once the program exists. */
export interface ChampionDraft { key: string; user_id: string; label: string }
export interface DetailsDraft {
  name: string; program_type: string; description: string; timezone: string;
  audience_mode: AudienceMode; default_assignment_type: AssignmentType; starts_at: string; ends_at: string;
  /** Phase 5: optional readiness policy, written with PUT /readiness-policy once the program exists. */
  readiness_policy?: ReadinessPolicy | null;
}
/** Program-level audience rule (the default for every session without its own). */
export interface ProgramRuleDraft { name: string; rule: RuleNode | null; assignment_type: AssignmentType | ''; mode: AudienceMode | '' }

const newSession = (): SessionDraft => ({
  key: Math.random().toString(36).slice(2), title: '', starts_local: '', ends_local: '', delivery_mode: 'in_person',
  location: '', meeting_url: '', trainer_label: '', objectives: '', assignment_type: '', user_ids: [], override: false, override_rule: null, override_name: '', preview: null,
  generate_meeting_link: false, tracks: [],
});
const ruleLess = (tr: TrackInput) => !tr.rule;
const emptyProgramRule = (): ProgramRuleDraft => ({ name: '', rule: null, assignment_type: '', mode: '' });

/** Pure validation shared by the Review step and the tests. Rule checks mirror the server's structural limits only. */
export function validateWizard(t: (k: string, d: string, o?: any) => string, details: DetailsDraft, sessions: SessionDraft[], programRule?: ProgramRuleDraft | null): string[] {
  const errs: string[] = [];
  if (!details.name.trim()) errs.push(t('training:wiz_err_name', 'Give the program a name.'));
  if (sessions.length === 0) errs.push(t('training:wiz_err_no_sessions', 'Add at least one session before publishing.'));
  if (programRule?.rule && validateRule(programRule.rule).length > 0) errs.push(t('training:wiz_err_program_rule', 'Finish the program audience rule (every condition needs a field and a value).'));
  sessions.forEach((s, i) => {
    const n = s.title.trim() || t('training:wiz_session_n', 'Session {{n}}', { n: i + 1 });
    if (!s.title.trim()) errs.push(t('training:wiz_err_session_title', '{{name}} needs a title.', { name: n }));
    if (!s.starts_local || !s.ends_local) errs.push(t('training:wiz_err_session_times', '{{name}} needs a start and end time.', { name: n }));
    else if (new Date(s.ends_local).getTime() < new Date(s.starts_local).getTime()) errs.push(t('training:wiz_err_session_order', '{{name}}: end must be at or after start.', { name: n }));
    if (s.override && (!s.override_rule || validateRule(s.override_rule).length > 0)) errs.push(t('training:wiz_err_session_rule', '{{name}}: finish the audience override or turn it off.', { name: n }));
    const tracks = s.tracks || [];
    tracks.forEach((tr, j) => {
      if (!tr.name.trim()) errs.push(t('training:wiz_err_track_name', '{{name}}: track {{n}} needs a name.', { name: n, n: j + 1 }));
      if (tr.rule && validateRule(tr.rule).length > 0) errs.push(t('training:wiz_err_track_rule', '{{name}}: finish the rule on track "{{track}}".', { name: n, track: tr.name.trim() || String(j + 1) }));
    });
    if (tracks.filter(ruleLess).length > 1) errs.push(t('training:wiz_err_track_default', '{{name}}: only one track can be the default (without a rule).', { name: n }));
  });
  return errs;
}

const selectCls = 'w-full h-9 rounded-md border border-border bg-background px-2 text-sm';

export type WizardStart = 'template' | 'blank';

interface Props {
  open: boolean;
  onClose: () => void;
  onDone?: (program: Program) => void;
  /** Pre-select the Start choice (e.g. "New from template" opens the gallery directly). */
  initialStart?: WizardStart | null;
}

export const ProgramWizard: React.FC<Props> = ({ open, onClose, onDone, initialStart = null }) => {
  const t = useT();
  const [step, setStep] = useState(0);
  const [start, setStart] = useState<WizardStart | null>(initialStart);
  const [readinessOpen, setReadinessOpen] = useState(false);
  const [readinessWritten, setReadinessWritten] = useState(false);
  const [details, setDetails] = useState<DetailsDraft>({
    name: '', program_type: '', description: '', timezone: browserTimezone(), audience_mode: 'dynamic', default_assignment_type: 'required', starts_at: '', ends_at: '',
  });
  const [sessions, setSessions] = useState<SessionDraft[]>([newSession()]);
  const [programRule, setProgramRule] = useState<ProgramRuleDraft>(emptyProgramRule());
  const [programPreview, setProgramPreview] = useState<AudiencePreview | null>(null);
  const [customType, setCustomType] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [createdSessions, setCreatedSessions] = useState<Record<string, string>>({});
  const [ruleWritten, setRuleWritten] = useState<Record<string, boolean>>({});
  const [createdTracks, setCreatedTracks] = useState<Record<string, string>>({});
  const [createdChampions, setCreatedChampions] = useState<Record<string, string>>({});
  const [champions, setChampions] = useState<ChampionDraft[]>([]);
  const [championPick, setChampionPick] = useState({ user_id: '', label: '', search: '' });
  const [trackEditor, setTrackEditor] = useState<{ sessionKey: string; trackKey?: string } | null>(null);
  const [policyEditorOpen, setPolicyEditorOpen] = useState(false);
  const [busy, setBusy] = useState<'draft' | 'publish' | null>(null);
  const [peopleSearch, setPeopleSearch] = useState('');

  // Dirty once there is a name to lose, or the person has moved past the
  // first step — both mean real input exists behind this dialog.
  useUnsavedGuard(open && (details.name.trim() !== '' || step > 0),
    'program-wizard', 'a learning programme');
  usePendingGuard(busy !== null, 'program-wizard', 'a learning programme');
  const [expandedPeople, setExpandedPeople] = useState<Record<string, boolean>>({});

  const types = useProgramTypes(open);
  const facetsQ = useAudienceFacets(open);
  const vocab = useRuleVocabulary(t, facetsQ.data);
  const people = vocab.people;
  const createType = useCreateProgramType();
  const preview = useAudiencePreview();
  const createProgram = useCreateProgram();
  const createSession = useCreateSession();
  const include = useIncludeParticipants();
  const putProgramRule = usePutProgramRule();
  const putSessionRule = usePutSessionRule();
  const createTrack = useCreateTrack();
  const addChampion = useAddChampion();
  const publish = usePublishProgram();
  const putReadiness = usePutReadinessPolicy();
  const policiesQ = useCompletionPolicies(open);
  const createPolicy = useCreateCompletionPolicy();
  const activePolicies = (policiesQ.data || []).filter((p) => p.active);

  const steps = [
    t('training:wiz_step_start', 'Start'), t('training:wiz_step_details', 'Details'), t('training:wiz_step_sessions', 'Sessions'),
    t('training:wiz_step_audience', 'Audience'), t('training:wiz_step_tracks', 'Tracks & responsibilities'), t('training:wiz_step_review', 'Review & publish'),
  ];
  const REVIEW = steps.length - 1;
  const [START, DETAILS, SESSIONS, AUDIENCE, TRACKS] = [0, 1, 2, 3, 4];
  const errors = useMemo(() => validateWizard(t, details, sessions, programRule), [details, sessions, programRule]);
  const personName = (id: string) => people.find((p) => p.user_id === id)?.name || id;
  const patchSession = (key: string, patch: Partial<SessionDraft>) => setSessions((xs) => xs.map((s) => (s.key === key ? { ...s, ...patch, ...('user_ids' in patch || 'override' in patch || 'override_rule' in patch ? { preview: null } : {}) } : s)));
  const patchProgramRule = (patch: Partial<ProgramRuleDraft>) => { setProgramRule((r) => ({ ...r, ...patch })); if ('rule' in patch) { setProgramPreview(null); setSessions((xs) => xs.map((s) => (s.override ? s : { ...s, preview: null }))); } };
  /** The rule a session will actually be resolved with (override wins, else the program rule). */
  const effectiveRule = (s: SessionDraft): RuleNode | null => (s.override ? s.override_rule : programRule.rule);
  const ruleOk = (r: RuleNode | null) => !!r && validateRule(r).length === 0;

  const reset = () => {
    setStep(0); setSessions([newSession()]); setProgramRule(emptyProgramRule()); setProgramPreview(null); setServerError(null); setCreatedId(null); setCreatedSessions({}); setRuleWritten({}); setBusy(null);
    setCreatedTracks({}); setCreatedChampions({}); setChampions([]); setChampionPick({ user_id: '', label: '', search: '' }); setTrackEditor(null); setPolicyEditorOpen(false);
    setDetails({ name: '', program_type: '', description: '', timezone: browserTimezone(), audience_mode: 'dynamic', default_assignment_type: 'required', starts_at: '', ends_at: '', readiness_policy: null });
    setStart(initialStart); setReadinessOpen(false); setReadinessWritten(false);
  };
  const close = () => { reset(); onClose(); };

  const toSessionInput = (s: SessionDraft, i: number): SessionInput => ({
    title: s.title.trim(), sequence: i + 1, starts_at: localInputToIso(s.starts_local) as string, ends_at: localInputToIso(s.ends_local) as string,
    timezone: details.timezone || undefined, delivery_mode: s.delivery_mode, location: s.location || undefined, meeting_url: s.meeting_url || undefined,
    trainer_label: s.trainer_label || undefined, objectives: s.objectives || undefined, materials: [],
    generate_meeting_link: (s.delivery_mode === 'virtual' || s.delivery_mode === 'hybrid') && s.generate_meeting_link && !s.meeting_url ? true : undefined,
    // Phase 1 facets stay as the fallback carrier for the per-session assignment type; matching comes from the rules.
    audience: { departments: [], employment_types: [], assignment_type: s.assignment_type || undefined, mode: details.audience_mode },
    // Phase 4 bindings only when chosen (absent = none).
    ...(s.completion_policy_id ? { completion_policy_id: s.completion_policy_id } : {}),
    ...(s.practice_id ? { practice_id: s.practice_id } : {}),
    ...(s.critical ? { critical: true } : {}),
  });

  const submit = async (mode: 'draft' | 'publish') => {
    setServerError(null);
    if (mode === 'publish' && errors.length) return;
    if (!details.name.trim()) { setServerError(t('training:wiz_err_name', 'Give the program a name.')); return; }
    setBusy(mode);
    try {
      let programId = createdId;
      let program: Program | null = null;
      if (!programId) {
        const body: ProgramInput = {
          name: details.name.trim(), description: details.description || undefined, program_type: details.program_type || undefined,
          timezone: details.timezone || undefined, audience_mode: details.audience_mode, default_assignment_type: details.default_assignment_type,
          starts_at: details.starts_at ? new Date(details.starts_at).toISOString() : undefined, ends_at: details.ends_at ? new Date(details.ends_at).toISOString() : undefined,
        };
        program = await createProgram.mutateAsync(body);
        programId = program.id; setCreatedId(programId);
      }
      // Readiness policy (Phase 5) is a separate PUT once the program exists; skipped when not enabled.
      if (details.readiness_policy?.enabled && !readinessWritten) {
        await putReadiness.mutateAsync({ programId, policy: details.readiness_policy });
        setReadinessWritten(true);
      }
      const written = { ...ruleWritten };
      // Rules are written BEFORE publish so the first resolution already uses them.
      if (ruleOk(programRule.rule) && !written.program) {
        await putProgramRule.mutateAsync({ programId, body: {
          name: programRule.name.trim() || details.name.trim(), rule: programRule.rule,
          assignment_type: programRule.assignment_type || undefined, mode: programRule.mode || undefined,
        } });
        written.program = true; setRuleWritten({ ...written });
      }
      const madeChampions = { ...createdChampions };
      for (const c of champions) {
        if (madeChampions[c.key]) continue;
        const r = await addChampion.mutateAsync({ user_id: c.user_id, scope_kind: 'program', scope_id: programId, label: c.label.trim() || undefined });
        madeChampions[c.key] = r.id; setCreatedChampions({ ...madeChampions });
      }
      const madeTracks = { ...createdTracks };
      const made = { ...createdSessions };
      const valid = sessions.filter((s) => s.title.trim() && s.starts_local && s.ends_local && new Date(s.ends_local).getTime() >= new Date(s.starts_local).getTime());
      for (let i = 0; i < valid.length; i++) {
        const s = valid[i];
        if (!made[s.key]) {
          const created = await createSession.mutateAsync({ programId, body: toSessionInput(s, i) });
          made[s.key] = created.id; setCreatedSessions({ ...made });
          if (s.user_ids.length) await include.mutateAsync({ sessionId: created.id, body: { user_ids: s.user_ids, assignment_type: s.assignment_type || undefined } });
        }
        if (s.override && ruleOk(s.override_rule) && !written[s.key]) {
          await putSessionRule.mutateAsync({ sessionId: made[s.key], body: {
            name: s.override_name.trim() || s.title.trim(), rule: s.override_rule, assignment_type: s.assignment_type || undefined,
          } });
          written[s.key] = true; setRuleWritten({ ...written });
        }
        // Tracks are created BEFORE publish so the first resolution already splits the roster.
        for (const tr of s.tracks || []) {
          const tk = `${s.key}:${tr.key}`;
          if (madeTracks[tk] || !tr.name.trim()) continue;
          const { key: _k, ...body } = tr;
          const created = await createTrack.mutateAsync({ sessionId: made[s.key], body });
          madeTracks[tk] = created.id; setCreatedTracks({ ...madeTracks });
        }
      }
      if (mode === 'publish') {
        const r = await publish.mutateAsync(programId);
        toast.success(t('training:wiz_published', 'Published — {{p}} participant(s) across {{s}} session(s)', { p: r.participants, s: r.sessions }));
        onDone?.(r.program);
      } else {
        toast.success(t('training:wiz_saved_draft', 'Saved as draft'));
        if (program) onDone?.(program);
      }
      close();
    } catch (e) {
      setServerError(getApiErrorMessage(e, t('training:err', 'Something went wrong')));
    } finally { setBusy(null); }
  };

  const runProgramPreview = () => preview.mutate({ rule: programRule.rule }, { onSuccess: (r) => setProgramPreview(r) });
  const runPreview = (s: SessionDraft) => preview.mutate(
    { rule: effectiveRule(s), user_ids: s.user_ids.length ? s.user_ids : undefined },
    { onSuccess: (r) => setSessions((xs) => xs.map((x) => (x.key === s.key ? { ...x, preview: r } : x))) },
  );
  const builderReady = !!facetsQ.data;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('training:wiz_title', 'New program')}</DialogTitle>
          <DialogDescription>{t('training:wiz_subtitle', 'Set up the program, its sessions and who should attend. Nothing is saved until you publish or save a draft.')}</DialogDescription>
        </DialogHeader>
        <Stepper steps={steps} current={step} className="flex-wrap" />

        {step === START && (
          <div className="space-y-3" data-testid="wizard-start">
            <p className="text-xs text-muted-foreground">{t('training:wiz_start_help', 'Start from a template (sessions, tracks, exercises and readiness rules pre-filled) or build a blank program.')}</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <button type="button" data-testid="wiz-start-template" aria-pressed={start === 'template'} onClick={() => setStart('template')}
                className={`rounded-md border p-3 text-left space-y-1 transition ${start === 'template' ? 'border-orange-400 bg-orange-50/50 dark:bg-orange-500/5' : 'border-border hover:bg-muted/50'}`}>
                <div className="flex items-center gap-2 text-sm font-medium text-foreground"><LayoutTemplate className="h-4 w-4 text-orange-500" aria-hidden />{t('training:wiz_start_template', 'Start from a template')}</div>
                <p className="text-xs text-muted-foreground">{t('training:wiz_start_template_help', 'Pick a template, choose a start date — a draft is created for you to set the audience and publish.')}</p>
              </button>
              <button type="button" data-testid="wiz-start-blank" aria-pressed={start === 'blank'} onClick={() => { setStart('blank'); setStep(DETAILS); }}
                className={`rounded-md border p-3 text-left space-y-1 transition ${start === 'blank' ? 'border-orange-400 bg-orange-50/50 dark:bg-orange-500/5' : 'border-border hover:bg-muted/50'}`}>
                <div className="flex items-center gap-2 text-sm font-medium text-foreground"><FilePlus2 className="h-4 w-4 text-orange-500" aria-hidden />{t('training:wiz_start_blank', 'Blank program')}</div>
                <p className="text-xs text-muted-foreground">{t('training:wiz_start_blank_help', 'Describe the program, its sessions and audience step by step.')}</p>
              </button>
            </div>
            {start === 'template' && (
              <TemplateGallery onCreated={(p) => { onDone?.(p); close(); }} />
            )}
          </div>
        )}

        {step === DETAILS && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="pw-name" className="text-xs">{t('training:wiz_name', 'Program name')}</Label>
              <Input id="pw-name" value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} placeholder={t('training:wiz_name_ph', 'e.g. New-hire onboarding 2026')} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pw-type" className="text-xs">{t('training:wiz_type', 'Program type')}</Label>
              <select id="pw-type" className={selectCls} value={details.program_type} onChange={(e) => { if (e.target.value === '__custom') { setShowCustom(true); } else setDetails({ ...details, program_type: e.target.value }); }}>
                <option value="">{t('training:pick_course', 'Select…')}</option>
                {(types.data || []).map((x) => <option key={x.id} value={x.key}>{x.label}</option>)}
                <option value="__custom">{t('training:wiz_add_type', '+ Add custom type')}</option>
              </select>
              {showCustom && (
                <div className="flex gap-1.5 pt-1">
                  <Input aria-label={t('training:wiz_custom_type', 'Custom type label')} className="h-8" value={customType} onChange={(e) => setCustomType(e.target.value)} placeholder={t('training:wiz_custom_type', 'Custom type label')} />
                  <Button size="sm" variant="outline" disabled={!customType.trim() || createType.isPending} onClick={() => {
                    const label = customType.trim();
                    const key = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
                    createType.mutate({ key, label }, { onSuccess: (pt) => { setDetails((d) => ({ ...d, program_type: pt.key })); setCustomType(''); setShowCustom(false); } });
                  }}>{createType.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:add_cert', 'Add')}</Button>
                </div>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="pw-tz" className="text-xs">{t('training:wiz_timezone', 'Time zone')}</Label>
              <Input id="pw-tz" value={details.timezone} onChange={(e) => setDetails({ ...details, timezone: e.target.value })} />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="pw-desc" className="text-xs">{t('training:description', 'Description')}</Label>
              <Textarea id="pw-desc" rows={3} value={details.description} onChange={(e) => setDetails({ ...details, description: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pw-mode" className="text-xs">{t('training:wiz_audience_mode', 'Audience mode')}</Label>
              <select id="pw-mode" className={selectCls} value={details.audience_mode} onChange={(e) => setDetails({ ...details, audience_mode: e.target.value as AudienceMode })}>
                <option value="dynamic">{t('training:audience_dynamic', 'Dynamic — new matches are added automatically')}</option>
                <option value="fixed">{t('training:audience_fixed', 'Fixed — roster frozen at publish')}</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="pw-atype" className="text-xs">{t('training:wiz_default_type', 'Default assignment type')}</Label>
              <select id="pw-atype" className={selectCls} value={details.default_assignment_type} onChange={(e) => setDetails({ ...details, default_assignment_type: e.target.value as AssignmentType })}>
                <option value="required">{t('training:required', 'Required')}</option>
                <option value="recommended">{t('training:recommended', 'Recommended')}</option>
                <option value="optional">{t('training:optional', 'Optional')}</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="pw-start" className="text-xs">{t('training:wiz_starts', 'Starts (optional)')}</Label>
              <Input id="pw-start" type="date" value={details.starts_at} onChange={(e) => setDetails({ ...details, starts_at: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pw-end" className="text-xs">{t('training:wiz_ends', 'Ends (optional)')}</Label>
              <Input id="pw-end" type="date" value={details.ends_at} onChange={(e) => setDetails({ ...details, ends_at: e.target.value })} />
            </div>
            <section className="sm:col-span-2 rounded-md border border-border p-3 space-y-2" data-testid="wizard-readiness">
              <button type="button" className="flex items-center gap-1 text-sm font-medium text-foreground" onClick={() => setReadinessOpen((o) => !o)} aria-expanded={readinessOpen}>
                {readinessOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                {t('training:wiz_readiness_title', 'Readiness (optional)')}
                {details.readiness_policy?.enabled && <span className="ml-1 rounded-full bg-success-soft text-success-strong px-2 py-0.5 text-[10px] font-medium">{t('training:rp_on', 'On')}</span>}
              </button>
              {readinessOpen && (
                <ReadinessPolicyEditor idPrefix="pw-rp" value={details.readiness_policy} onChange={(policy) => setDetails({ ...details, readiness_policy: policy })} />
              )}
            </section>
          </div>
        )}

        {step === SESSIONS && (
          <div className="space-y-3">
            {sessions.map((s, i) => (
              <div key={s.key} className="rounded-md border border-border p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-foreground">{t('training:wiz_session_n', 'Session {{n}}', { n: i + 1 })}</div>
                  <button type="button" aria-label={t('training:wiz_remove_session', 'Remove session')} className="text-muted-foreground hover:text-red-500" onClick={() => setSessions((xs) => xs.filter((x) => x.key !== s.key))}><Trash2 className="h-4 w-4" /></button>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div className="space-y-1 sm:col-span-2">
                    <Label htmlFor={`s-title-${s.key}`} className="text-xs">{t('training:course_title', 'Title')}</Label>
                    <Input id={`s-title-${s.key}`} className="h-9" value={s.title} onChange={(e) => patchSession(s.key, { title: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`s-start-${s.key}`} className="text-xs">{t('training:wiz_start_time', 'Starts')}</Label>
                    <Input id={`s-start-${s.key}`} className="h-9" type="datetime-local" value={s.starts_local} onChange={(e) => patchSession(s.key, { starts_local: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`s-end-${s.key}`} className="text-xs">{t('training:wiz_end_time', 'Ends')}</Label>
                    <Input id={`s-end-${s.key}`} className="h-9" type="datetime-local" value={s.ends_local} onChange={(e) => patchSession(s.key, { ends_local: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`s-mode-${s.key}`} className="text-xs">{t('training:delivery_mode', 'Delivery mode')}</Label>
                    <select id={`s-mode-${s.key}`} className={selectCls} value={s.delivery_mode} onChange={(e) => patchSession(s.key, { delivery_mode: e.target.value as DeliveryMode })}>
                      <option value="in_person">{t('training:mode_in_person', 'In person')}</option>
                      <option value="virtual">{t('training:mode_virtual', 'Virtual')}</option>
                      <option value="hybrid">{t('training:mode_hybrid', 'Hybrid')}</option>
                      <option value="self_paced">{t('training:mode_self_paced', 'Self-paced')}</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`s-trainer-${s.key}`} className="text-xs">{t('training:trainer', 'Trainer')}</Label>
                    <Input id={`s-trainer-${s.key}`} className="h-9" value={s.trainer_label} onChange={(e) => patchSession(s.key, { trainer_label: e.target.value })} />
                  </div>
                  {s.delivery_mode !== 'virtual' && (
                    <div className="space-y-1">
                      <Label htmlFor={`s-loc-${s.key}`} className="text-xs">{t('training:location', 'Location')}</Label>
                      <Input id={`s-loc-${s.key}`} className="h-9" value={s.location} onChange={(e) => patchSession(s.key, { location: e.target.value })} />
                    </div>
                  )}
                  {s.delivery_mode !== 'in_person' && (
                    <div className="space-y-1">
                      <Label htmlFor={`s-url-${s.key}`} className="text-xs">{t('training:meeting_url', 'Meeting link')}</Label>
                      <Input id={`s-url-${s.key}`} className="h-9" value={s.meeting_url} onChange={(e) => patchSession(s.key, { meeting_url: e.target.value })} placeholder="https://…" />
                    </div>
                  )}
                  {(s.delivery_mode === 'virtual' || s.delivery_mode === 'hybrid') && (
                    <div className="space-y-1 sm:col-span-2">
                      <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                        <input type="checkbox" className="accent-orange-500" checked={s.generate_meeting_link} disabled={!!s.meeting_url} onChange={(e) => patchSession(s.key, { generate_meeting_link: e.target.checked })} />
                        {t('training:mint_link', 'Mint a meeting link automatically')}
                      </label>
                      <p className="text-[11px] text-muted-foreground">{s.meeting_url ? t('training:mint_link_pasted', 'You pasted a link, so nothing will be minted.') : t('training:mint_link_help', 'On publish the platform creates a Meet/Teams link through the company calendar and puts it on the session.')}</p>
                    </div>
                  )}
                  <div className="space-y-1 sm:col-span-2">
                    <Label htmlFor={`s-obj-${s.key}`} className="text-xs">{t('training:objectives', 'Objectives')}</Label>
                    <Textarea id={`s-obj-${s.key}`} rows={2} value={s.objectives} onChange={(e) => patchSession(s.key, { objectives: e.target.value })} />
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                      <input type="checkbox" className="accent-orange-500" checked={!!s.critical} onChange={(e) => patchSession(s.key, { critical: e.target.checked })} data-testid={`s-critical-${i}`} />
                      {t('training:critical_for_readiness', 'Critical for readiness')}
                    </label>
                    <p className="text-[11px] text-muted-foreground">{t('training:critical_help', 'When the readiness policy requires critical sessions, missing this one makes a participant "not ready".')}</p>
                  </div>
                </div>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setSessions((xs) => [...xs, newSession()])}><Plus className="mr-1 h-4 w-4" />{t('training:wiz_add_session', 'Add session')}</Button>
          </div>
        )}

        {step === AUDIENCE && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">{t('training:wiz_audience_help_v2', 'Build one audience rule for the whole program; any session can override it. Rules are matched by the platform — use Preview to see who qualifies today.')}</p>
            {facetsQ.isLoading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('training:loading', 'Loading')}…</div>}
            {facetsQ.isError && <p className="text-sm text-red-600 dark:text-red-400">{getApiErrorMessage(facetsQ.error, t('training:facets_unavailable', 'The rule builder vocabulary could not be loaded.'))}</p>}

            <section className="rounded-md border border-border p-3 space-y-2" data-testid="program-rule">
              <div className="text-sm font-medium text-foreground">{t('training:wiz_program_rule', 'Program audience')}</div>
              <div className="grid gap-2 sm:grid-cols-3">
                <div className="space-y-1 sm:col-span-3">
                  <Label htmlFor="pw-rule-name" className="text-xs">{t('training:rule_name', 'Rule name')}</Label>
                  <Input id="pw-rule-name" className="h-9" value={programRule.name} onChange={(e) => patchProgramRule({ name: e.target.value })} placeholder={details.name.trim() || t('training:rule_name_ph', 'e.g. Finance + HR')} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="pw-rule-atype" className="text-xs">{t('training:assignment_type', 'Assignment type')}</Label>
                  <select id="pw-rule-atype" className={selectCls} value={programRule.assignment_type} onChange={(e) => patchProgramRule({ assignment_type: e.target.value as AssignmentType | '' })}>
                    <option value="">{t('training:wiz_program_default', 'Program default')} ({details.default_assignment_type})</option>
                    <option value="required">{t('training:required', 'Required')}</option>
                    <option value="recommended">{t('training:recommended', 'Recommended')}</option>
                    <option value="optional">{t('training:optional', 'Optional')}</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="pw-rule-mode" className="text-xs">{t('training:wiz_audience_mode', 'Audience mode')}</Label>
                  <select id="pw-rule-mode" className={selectCls} value={programRule.mode} onChange={(e) => patchProgramRule({ mode: e.target.value as AudienceMode | '' })}>
                    <option value="">{t('training:wiz_program_default', 'Program default')} ({details.audience_mode})</option>
                    <option value="dynamic">{t('training:audience_dynamic_short', 'Dynamic')}</option>
                    <option value="fixed">{t('training:audience_fixed_short', 'Fixed')}</option>
                  </select>
                </div>
              </div>
              {builderReady && (
                <ConditionGroupBuilder idPrefix="pw-rule" facets={vocab.facets} options={vocab.options} people={vocab.people} labels={vocab.labels} value={programRule.rule} onChange={(r) => patchProgramRule({ rule: r })} />
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" disabled={preview.isPending || !ruleOk(programRule.rule)} onClick={runProgramPreview} data-testid="preview-program-rule">
                  {preview.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:wiz_preview', 'Preview matched employees')}
                </Button>
                {!programRule.rule && <span className="text-xs text-muted-foreground">{t('training:wiz_no_program_rule', 'No program rule — only sessions with their own override or specific people will have participants.')}</span>}
              </div>
              {programPreview && <AudiencePreviewPanel preview={programPreview} />}
            </section>

            {sessions.length === 0 && <p className="text-sm text-muted-foreground">{t('training:wiz_err_no_sessions', 'Add at least one session before publishing.')}</p>}
            {sessions.map((s, i) => {
              const filtered = people.filter((p) => !peopleSearch || p.name.toLowerCase().includes(peopleSearch.toLowerCase()));
              return (
                <div key={s.key} className="rounded-md border border-border p-3 space-y-2" data-testid={`session-audience-${i}`}>
                  <div className="text-sm font-medium text-foreground">{s.title.trim() || t('training:wiz_session_n', 'Session {{n}}', { n: i + 1 })}</div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label htmlFor={`a-type-${s.key}`} className="text-xs">{t('training:assignment_type', 'Assignment type')}</Label>
                      <select id={`a-type-${s.key}`} className={selectCls} value={s.assignment_type} onChange={(e) => patchSession(s.key, { assignment_type: e.target.value as AssignmentType | '' })}>
                        <option value="">{t('training:wiz_program_default', 'Program default')} ({programRule.assignment_type || details.default_assignment_type})</option>
                        <option value="required">{t('training:required', 'Required')}</option>
                        <option value="recommended">{t('training:recommended', 'Recommended')}</option>
                        <option value="optional">{t('training:optional', 'Optional')}</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                      <input type="checkbox" className="accent-orange-500" checked={s.override} onChange={(e) => patchSession(s.key, { override: e.target.checked })} />
                      {t('training:wiz_override_session', 'Override for this session')}
                    </label>
                    {s.override && (
                      <div className="mt-1.5 rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-500/10 p-2 space-y-2">
                        <p className="text-[11px] text-muted-foreground">{t('training:wiz_override_help', 'This rule replaces the program rule for this session only.')}</p>
                        <div className="space-y-1">
                          <Label htmlFor={`s-rule-name-${s.key}`} className="text-xs">{t('training:rule_name', 'Rule name')}</Label>
                          <Input id={`s-rule-name-${s.key}`} className="h-8" value={s.override_name} onChange={(e) => patchSession(s.key, { override_name: e.target.value })} placeholder={s.title.trim() || t('training:rule_name_ph', 'e.g. Finance + HR')} />
                        </div>
                        {builderReady && (
                          <ConditionGroupBuilder idPrefix={`pw-s-${s.key}`} facets={vocab.facets} options={vocab.options} people={vocab.people} labels={vocab.labels} value={s.override_rule} onChange={(r) => patchSession(s.key, { override_rule: r })} />
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <button type="button" className="flex items-center gap-1 text-xs font-medium" onClick={() => setExpandedPeople((m) => ({ ...m, [s.key]: !m[s.key] }))}>
                      {expandedPeople[s.key] ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      {t('training:wiz_explicit_people', 'Specific people')} {s.user_ids.length > 0 && `· ${s.user_ids.length}`}
                    </button>
                    {expandedPeople[s.key] && (
                      <div className="mt-1 rounded-md border border-border p-2 space-y-1.5">
                        <Input aria-label={t('training:search_people', 'Search people')} className="h-8" placeholder={t('training:search_people', 'Search people')} value={peopleSearch} onChange={(e) => setPeopleSearch(e.target.value)} />
                        <div className="max-h-36 overflow-y-auto space-y-1">
                          {filtered.slice(0, 100).map((p) => (
                            <label key={p.user_id} className="flex items-center gap-2 text-sm cursor-pointer">
                              <input type="checkbox" className="accent-orange-500" checked={s.user_ids.includes(p.user_id)} onChange={(e) => patchSession(s.key, { user_ids: e.target.checked ? [...s.user_ids, p.user_id] : s.user_ids.filter((x) => x !== p.user_id) })} />
                              {p.name}{p.department ? <span className="text-xs text-muted-foreground">· {p.department}</span> : null}
                            </label>
                          ))}
                          {filtered.length === 0 && <div className="text-xs text-muted-foreground">{t('training:no_people_match', 'No one matches.')}</div>}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" disabled={preview.isPending || (!ruleOk(effectiveRule(s)) && s.user_ids.length === 0)} onClick={() => runPreview(s)} data-testid={`preview-session-${i}`}>
                      {preview.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:wiz_preview', 'Preview matched employees')}
                    </Button>
                    <span className="text-xs text-muted-foreground">{s.override ? t('training:wiz_uses_override', 'Uses this session\'s override') : programRule.rule ? t('training:wiz_uses_program_rule', 'Uses the program rule') : t('training:wiz_uses_people_only', 'Specific people only')}</span>
                  </div>
                  {s.preview && <AudiencePreviewPanel preview={s.preview} />}
                </div>
              );
            })}
          </div>
        )}

        {step === TRACKS && (
          <div className="space-y-3" data-testid="tracks-step">
            <section className="rounded-md border border-border p-3 space-y-2" data-testid="wizard-completion">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium text-foreground">{t('training:wiz_completion_title', 'Completion requirements')}</div>
                {!policyEditorOpen && <Button size="sm" variant="outline" onClick={() => setPolicyEditorOpen(true)} data-testid="wiz-new-policy"><Plus className="mr-1 h-4 w-4" />{t('training:policy_new', 'New policy')}</Button>}
              </div>
              <p className="text-[11px] text-muted-foreground">{t('training:wiz_completion_help', 'Optional: bind a completion policy (attendance, score, evidence, sign-offs, practical exercise) and a practical exercise to each session below — or to a track, which wins for its members. The platform evaluates them per participant.')}</p>
              {policiesQ.isLoading && <div className="text-xs text-muted-foreground">{t('training:loading', 'Loading')}…</div>}
              {!policiesQ.isLoading && activePolicies.length === 0 && !policyEditorOpen && (
                <p className="text-xs text-amber-700 dark:text-amber-300">{t('training:wiz_no_policies', 'No completion policies yet — create one here to require more than "mark complete".')}</p>
              )}
              {activePolicies.length > 0 && (
                <ul className="flex flex-wrap gap-1.5">
                  {activePolicies.map((p) => (
                    <li key={p.id} className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground"><span className="font-medium text-foreground">{p.name}</span> · {modeLabel(t, p.mode)} · {t('training:requirements_n', '{{n}} requirement(s)', { n: p.requirements.length })}</li>
                  ))}
                </ul>
              )}
              {policyEditorOpen && (
                <PolicyEditor idPrefix="pw-policy" className="rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-500/10 p-3" busy={createPolicy.isPending}
                  onCancel={() => setPolicyEditorOpen(false)}
                  onSave={(input) => createPolicy.mutate(input, { onSuccess: () => { toast.success(t('training:policy_saved', 'Policy saved')); setPolicyEditorOpen(false); } })} />
              )}
            </section>
            <p className="text-xs text-muted-foreground">{t('training:wiz_tracks_help', 'Optional: split a session into tracks (e.g. drivers vs. office staff) with their own objectives and instructions. A track without a rule is the default for everyone else; the platform places each participant in the first matching track.')}</p>
            {sessions.map((s, i) => {
              const tracks = s.tracks || [];
              const editing = trackEditor?.sessionKey === s.key ? trackEditor : null;
              const editingTrack = editing?.trackKey ? tracks.find((x) => x.key === editing.trackKey) : null;
              return (
                <div key={s.key} className="rounded-md border border-border p-3 space-y-2" data-testid={`session-tracks-${i}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-medium text-foreground">{s.title.trim() || t('training:wiz_session_n', 'Session {{n}}', { n: i + 1 })}</div>
                    {!editing && <Button size="sm" variant="outline" onClick={() => setTrackEditor({ sessionKey: s.key })}><Plus className="mr-1 h-4 w-4" />{t('training:track_add', 'Add track')}</Button>}
                  </div>
                  <PolicyPracticeSelects idPrefix={`pw-bind-${s.key}`} policyId={s.completion_policy_id || ''} practiceId={s.practice_id || ''}
                    onChange={(p) => patchSession(s.key, { ...(p.policyId !== undefined ? { completion_policy_id: p.policyId } : {}), ...(p.practiceId !== undefined ? { practice_id: p.practiceId } : {}) })} />
                  {tracks.length === 0 && !editing && <p className="text-xs text-muted-foreground">{t('training:wiz_no_tracks', 'No tracks — everyone follows the session as one group.')}</p>}
                  {tracks.length > 0 && (
                    <ol className="divide-y divide-border rounded-md border border-border">
                      {[...tracks].sort((a, b) => (a.sequence || 0) - (b.sequence || 0)).map((tr) => (
                        <li key={tr.key} className="flex flex-wrap items-center justify-between gap-2 px-2.5 py-1.5 text-sm">
                          <div className="min-w-0">
                            <span className="text-foreground">{tr.sequence ? `${tr.sequence}. ` : ''}{tr.name || '—'}</span>
                            <span className="text-xs text-muted-foreground"> · {tr.rule ? vocab.sentence(tr.rule) : t('training:track_default', 'Default track (no rule)')}{tr.assignment_type ? ` · ${tr.assignment_type}` : ''}</span>
                          </div>
                          <div className="flex gap-1">
                            <button type="button" aria-label={t('training:track_edit_named', 'Edit track {{name}}', { name: tr.name })} className="text-muted-foreground hover:text-foreground" onClick={() => setTrackEditor({ sessionKey: s.key, trackKey: tr.key })}><Pencil className="h-4 w-4" /></button>
                            <button type="button" aria-label={t('training:track_remove_named', 'Remove track {{name}}', { name: tr.name })} className="text-muted-foreground hover:text-red-500" onClick={() => patchSession(s.key, { tracks: tracks.filter((x) => x.key !== tr.key) })}><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                  {editing && (
                    <TrackEditor key={editing.trackKey || 'new'} idPrefix={`pw-track-${s.key}`} initial={editingTrack || null} defaultSequence={tracks.length + 1}
                      onCancel={() => setTrackEditor(null)}
                      onSave={(input) => {
                        const next = editingTrack ? tracks.map((x) => (x.key === editingTrack.key ? { ...input, key: x.key } : x)) : [...tracks, { ...input, key: Math.random().toString(36).slice(2) }];
                        patchSession(s.key, { tracks: next }); setTrackEditor(null);
                      }} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {step === REVIEW && (
          <div className="space-y-3">
            <div className="rounded-md border border-border p-3 text-sm space-y-1">
              <div className="font-medium text-foreground">{details.name || '—'}</div>
              <div className="text-xs text-muted-foreground">
                {(types.data || []).find((x) => x.key === details.program_type)?.label || details.program_type || t('training:wiz_no_type', 'No type')} · {details.timezone} · {details.audience_mode} · <AssignmentTypeBadge type={details.default_assignment_type} />
              </div>
              {details.description && <p className="text-xs text-muted-foreground">{details.description}</p>}
              {details.readiness_policy?.enabled && (
                <div className="text-xs text-muted-foreground" data-testid="review-readiness">
                  <span className="font-medium text-foreground">{t('training:readiness', 'Readiness')}:</span> {t('training:rp_summary_pct', '{{pct}}% attendance', { pct: details.readiness_policy.attendance_pct })}
                  {details.readiness_policy.require_critical_sessions ? ` · ${t('training:rp_critical_short', 'critical sessions')}` : ''}
                  {details.readiness_policy.require_practices ? ` · ${t('training:rp_practices_short', 'practices validated')}` : ''}
                  {details.readiness_policy.require_verification ? ` · ${t('training:rp_verification_short', 'verified')}` : ''}
                </div>
              )}
              <div className="text-xs text-muted-foreground" data-testid="review-program-rule">
                <span className="font-medium text-foreground">{t('training:wiz_program_rule', 'Program audience')}:</span> {programRule.rule ? vocab.sentence(programRule.rule) : t('training:rule_none', 'No rule')}
                {programRule.rule && programRule.name.trim() ? ` · ${programRule.name.trim()}` : ''}
                {programPreview ? ` · ${t('training:wiz_preview_total', '{{n}} people match', { n: programPreview.total })}` : ''}
              </div>
            </div>
            <ol className="space-y-1.5">
              {sessions.map((s, i) => (
                <li key={s.key} className="rounded-md border border-border p-2.5 text-sm">
                  <div className="font-medium text-foreground">{i + 1}. {s.title || t('training:wiz_session_n', 'Session {{n}}', { n: i + 1 })}</div>
                  <div className="text-xs text-muted-foreground">
                    {s.starts_local ? new Date(s.starts_local).toLocaleString() : '—'} → {s.ends_local ? new Date(s.ends_local).toLocaleString() : '—'} · {s.delivery_mode}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {s.override ? `${t('training:wiz_override_label', 'Override')}: ${vocab.sentence(s.override_rule)}` : programRule.rule ? t('training:wiz_uses_program_rule', 'Uses the program rule') : t('training:wiz_uses_people_only', 'Specific people only')}
                    {s.assignment_type ? ` · ${s.assignment_type}` : ''}
                    {s.user_ids.length ? ` · ${t('training:wiz_explicit_people', 'Specific people')}: ${s.user_ids.map(personName).join(', ')}` : ''}
                    {s.preview ? ` · ${t('training:wiz_preview_total', '{{n}} people match', { n: s.preview.total })}` : ''}
                    {(s.delivery_mode === 'virtual' || s.delivery_mode === 'hybrid') && s.generate_meeting_link && !s.meeting_url ? ` · ${t('training:mint_link_short', 'meeting link minted on publish')}` : ''}
                    {s.critical ? ` · ${t('training:critical', 'Critical')}` : ''}
                  </div>
                  {(s.tracks || []).length > 0 && (
                    <div className="text-xs text-muted-foreground">{t('training:tracks_n', '{{n}} track(s)', { n: s.tracks.length })}: {s.tracks.map((tr) => tr.name).join(', ')}</div>
                  )}
                  {(s.completion_policy_id || s.practice_id) && (
                    <div className="text-xs text-muted-foreground" data-testid={`review-binding-${i}`}>
                      {s.completion_policy_id ? `${t('training:completion_policy', 'Completion policy')}: ${(policiesQ.data || []).find((p) => p.id === s.completion_policy_id)?.name || s.completion_policy_id.slice(0, 8)}` : ''}
                      {s.completion_policy_id && s.practice_id ? ' · ' : ''}
                      {s.practice_id ? t('training:practice_bound', 'Practical exercise bound') : ''}
                    </div>
                  )}
                </li>
              ))}
            </ol>
            <section className="rounded-md border border-border p-3 space-y-2" data-testid="wizard-champions">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('training:champions', 'Champions')} <span className="font-normal normal-case">({t('training:optional', 'Optional')})</span></div>
              <p className="text-[11px] text-muted-foreground">{t('training:champions_help', 'Champions are the go-to people for this {{scope}}: they see the roster and attendance and are listed under "Need help?" for participants.', { scope: t('training:scope_program', 'program') })}</p>
              {champions.length > 0 && (
                <ul className="divide-y divide-border rounded-md border border-border">
                  {champions.map((c) => (
                    <li key={c.key} className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-sm">
                      <span>{personName(c.user_id)}{c.label ? <span className="text-xs text-muted-foreground"> · {c.label}</span> : null}</span>
                      <button type="button" aria-label={t('training:champion_remove', 'Remove {{name}}', { name: personName(c.user_id) })} className="text-muted-foreground hover:text-red-500" onClick={() => setChampions((xs) => xs.filter((x) => x.key !== c.key))}><Trash2 className="h-4 w-4" /></button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="grid gap-2 sm:grid-cols-2 items-end">
                <div className="space-y-1">
                  <Label htmlFor="pw-champ-person" className="text-xs">{t('training:champion', 'Champion')}</Label>
                  <PersonPicker id="pw-champ-person" className="" multiple={false}
                    people={people.filter((p) => !champions.some((c) => c.user_id === p.user_id))}
                    value={championPick.user_id ? [championPick.user_id] : []}
                    labels={{ ...vocab.labels, searchPeople: t('training:search_people_ph', 'Type a name or department…'), noMatches: t('training:no_people_match', 'No one matches.') }}
                    onChange={(ids) => setChampionPick({ ...championPick, user_id: ids[0] || '' })} />
                  {facetsQ.isSuccess && people.length === 0 && <p className="text-[11px] text-muted-foreground">{t('training:no_people_with_accounts', 'Only employees with a user account can be picked — none are linked yet.')}</p>}
                </div>
                <div className="flex gap-1.5">
                  <div className="space-y-1 flex-1">
                    <Label htmlFor="pw-champ-label" className="text-xs">{t('training:champion_label', 'Label (optional)')}</Label>
                    <Input id="pw-champ-label" className="h-8" value={championPick.label} onChange={(e) => setChampionPick({ ...championPick, label: e.target.value })} />
                  </div>
                  <Button size="sm" variant="outline" className="h-8 self-end" disabled={!championPick.user_id} onClick={() => { setChampions((xs) => [...xs, { key: Math.random().toString(36).slice(2), user_id: championPick.user_id, label: championPick.label }]); setChampionPick({ user_id: '', label: '', search: '' }); }}>{t('training:add_cert', 'Add')}</Button>
                </div>
              </div>
            </section>
            {errors.length > 0 && (
              <div role="alert" data-testid="wizard-errors" className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-500/10 p-3 text-sm space-y-1">
                <div className="flex items-center gap-1.5 font-medium text-amber-800 dark:text-amber-300"><AlertTriangle className="h-4 w-4" /> {t('training:wiz_fix_before_publish', 'Fix these before publishing')}</div>
                <ul className="list-disc pl-5 text-amber-800 dark:text-amber-200">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
              </div>
            )}
            {serverError && <div role="alert" className="rounded-md border border-red-300 bg-red-50 dark:bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-300">{serverError}</div>}
            {createdId && <p className="text-xs text-muted-foreground">{t('training:wiz_partial', 'The program was created; retrying continues from where it stopped.')}</p>}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-border">
          <Button variant="ghost" size="sm" disabled={step === START || !!busy} onClick={() => setStep((s) => s - 1)}>{t('training:wiz_back', 'Back')}</Button>
          <div className="flex gap-2">
            {step === START ? (
              <Button size="sm" variant="ghost" onClick={close}>{t('training:cancel', 'Cancel')}</Button>
            ) : step < REVIEW ? (
              <Button size="sm" onClick={() => setStep((s) => s + 1)} disabled={step === DETAILS && !details.name.trim()}>{t('training:wiz_next', 'Next')}</Button>
            ) : (
              <>
                <Button size="sm" variant="outline" disabled={!!busy || !details.name.trim()} onClick={() => submit('draft')}>
                  {busy === 'draft' ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:wiz_save_draft', 'Save as draft')}
                </Button>
                <Button size="sm" disabled={!!busy || errors.length > 0} onClick={() => submit('publish')}>
                  {busy === 'publish' ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:wiz_publish', 'Publish')}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProgramWizard;
